import { getValidationBadgeDetails } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/bankAccountValidation.utils.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { MipInvestorBankAccountValidationRes } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Flex, Text, Tooltip } from '@mantine/core';

interface Props {
	validation: MipInvestorBankAccountValidationRes | null;
}

/** States whether the bank data below it was validated against the provider's bank
 *  directory. Validation is about the data being right, not about the account
 *  belonging to this person — that is verification, and the list tracks it separately. */
export const ValidationBadge = ({ validation }: Props) => {
	const tMipManage = useTranslationMipManage();
	const { color, presentation } = getValidationBadgeDetails(validation);

	if (!presentation) {
		return null;
	}

	const checkedAt = validation
		? formatStringDateToUI(validation.checkedAt, DATE_FORMATS.DATE_TIME_ALT)
		: null;

	return (
		<Tooltip
			label={tMipManage('bank_accounts.details.validation.checked_at', {
				defaultValue: 'Checked {checkedAt} via {provider}',
				checkedAt,
				provider: validation?.provider,
			})}
			disabled={!checkedAt || !validation?.provider}
			withArrow
		>
			<Flex
				align="center"
				gap="0.25rem"
				px="0.5rem"
				py="0.125rem"
				w="fit-content"
				style={{ border: `1px solid ${color}`, borderRadius: '1rem' }}
				data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_BANK_ACCOUNT_VALIDATION_BADGE}
			>
				<Icon variant={presentation.iconVariant} size="md" color={color} />

				<Text size="sm" c={color}>
					{tMipManage(presentation.labelKey, { defaultValue: presentation.defaultValue })}
				</Text>
			</Flex>
		</Tooltip>
	);
};
